'use client';

import React, { createContext, useContext, useEffect, useState, useCallback } from 'react';

import { CONTRAST_STORAGE_KEY, DENSITY_STORAGE_KEY, THEME_STORAGE_KEY } from './themeScript';

/**
 * AURORA DS — sterowanie osiami motywu.
 *
 * DLACZEGO `data-*`, A NIE KLASA. Cała Aurora czyta atrybuty na `<html>`
 * (`:root[data-mode="light"]`, `:root[data-density="kiosk"]`,
 * `:root[data-contrast="high"]` — patrz `styles/aurora.css`). Poprzednia wersja
 * tego providera ustawiała wyłącznie klasę `light`/`dark` i `style.colorScheme`,
 * a w repo nie było ani jednego utility `dark:` — więc przełącznik motywu nie
 * zmieniał wyglądu aplikacji, tylko kontrolki systemowe (paski przewijania,
 * pickery daty) na jasne na ciemnej stronie. To był martwy przycisk i został
 * usunięty z interfejsu; wraca razem z tym plikiem.
 *
 * Klasa `light`/`dark` ZOSTAJE obok atrybutu — `darkMode: ['class']` siedzi w
 * każdym configu Tailwinda, więc gdy kiedyś pojawi się pierwsze `dark:`, będzie
 * miało na czym działać.
 *
 * Trzy osie, trzy niezależne preferencje:
 *   mode      dark | light | system   — paleta
 *   density   comfortable | compact | kiosk — wysokości kontrolek i skala odstępów
 *   contrast  normal | high           — mocniejsze obramowania i grubszy focus (a11y)
 */

export type ThemeMode = 'dark' | 'light' | 'system';
export type ThemeDensity = 'comfortable' | 'compact' | 'kiosk';
export type ThemeContrast = 'normal' | 'high';

interface ThemeContextValue {
  theme: ThemeMode;
  resolvedTheme: 'dark' | 'light';
  setTheme: (theme: ThemeMode) => void;
  toggleTheme: () => void;
  density: ThemeDensity;
  setDensity: (density: ThemeDensity) => void;
  contrast: ThemeContrast;
  setContrast: (contrast: ThemeContrast) => void;
}

const ThemeContext = createContext<ThemeContextValue | undefined>(undefined);

// Klucze i skrypt przed-malowaniem żyją w `themeScript.tsx` — bez `'use client'`,
// żeby `layout.tsx` (Server Component) mógł je zaimportować podścieżką, nie
// przez barrel, który ciągnie całą bibliotekę klienta.
export {
  THEME_STORAGE_KEY,
  DENSITY_STORAGE_KEY,
  CONTRAST_STORAGE_KEY,
  themeInitScript,
  ThemeScript,
} from './themeScript';

function systemMode(): 'dark' | 'light' {
  if (typeof window === 'undefined') return 'dark';
  return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
}

function readStored<T extends string>(key: string, allowed: readonly T[], fallback: T): T {
  if (typeof window === 'undefined') return fallback;
  try {
    const v = localStorage.getItem(key) as T | null;
    return v && allowed.includes(v) ? v : fallback;
  } catch {
    return fallback;
  }
}

function persist(key: string, value: string) {
  try {
    localStorage.setItem(key, value);
  } catch {
    /* storage zablokowany — preferencja żyje tylko do przeładowania */
  }
}

const MODES = ['dark', 'light', 'system'] as const;
const DENSITIES = ['comfortable', 'compact', 'kiosk'] as const;
const CONTRASTS = ['normal', 'high'] as const;

export function ThemeProvider({
  children,
  defaultTheme = 'dark',
  defaultDensity = 'comfortable',
  storageKey = THEME_STORAGE_KEY,
}: {
  children: React.ReactNode;
  defaultTheme?: ThemeMode;
  defaultDensity?: ThemeDensity;
  storageKey?: string;
}) {
  // Stan startowy = wartość z SSR; odczyt z localStorage dopiero po montażu,
  // żeby serwer i pierwszy render klienta dawały ten sam HTML (hydracja).
  const [theme, setThemeState] = useState<ThemeMode>(defaultTheme);
  const [resolvedTheme, setResolvedTheme] = useState<'dark' | 'light'>(
    defaultTheme === 'system' ? 'dark' : defaultTheme
  );
  const [density, setDensityState] = useState<ThemeDensity>(defaultDensity);
  const [contrast, setContrastState] = useState<ThemeContrast>('normal');
  /**
   * Czy odczyt preferencji ze storage już się odbył.
   *
   * Bez tej bramki mignięcie ciemnego wracało tą samą drogą, którą `themeScript`
   * zamyka: efekt poniżej tylko KOLEJKUJE stan z localStorage, a efekty piszące
   * do DOM biegną w TYM SAMYM przebiegu jeszcze ze stanem domyślnym
   * (dark/comfortable/normal) i nadpisują `data-mode="light"` ustawione przed
   * pierwszym malowaniem. Dopiero kolejny render przywracał wybór — czyli
   * użytkownik z zapisanym trybem jasnym (albo gęstością compact, albo wysokim
   * kontrastem) widział dokładnie tę klatkę ciemnego, której skrypt miał nie
   * dopuścić. Dopóki nie wiemy, co jest w storage, atrybuty skryptu zostają
   * nietknięte — a gdy skryptu nie ma, bazą jest goły `:root`, czyli ciemny
   * (patrz `aurora.css`: `:root, :root[data-mode="dark"]`).
   */
  const [storageRead, setStorageRead] = useState(false);

  useEffect(() => {
    setThemeState(readStored(storageKey, MODES, defaultTheme));
    setDensityState(readStored(DENSITY_STORAGE_KEY, DENSITIES, defaultDensity));
    setContrastState(readStored(CONTRAST_STORAGE_KEY, CONTRASTS, 'normal'));
    setStorageRead(true);
  }, [storageKey, defaultTheme, defaultDensity]);

  // ——— mode → data-mode (+ klasa i color-scheme dla kontrolek systemowych)
  useEffect(() => {
    if (!storageRead) return;
    const resolved = theme === 'system' ? systemMode() : theme;
    setResolvedTheme(resolved);
    const root = document.documentElement;
    root.setAttribute('data-mode', resolved);
    root.classList.remove('light', 'dark');
    root.classList.add(resolved);
    root.style.colorScheme = resolved;
  }, [theme, storageRead]);

  useEffect(() => {
    if (!storageRead) return;
    document.documentElement.setAttribute('data-density', density);
  }, [density, storageRead]);

  useEffect(() => {
    if (!storageRead) return;
    const root = document.documentElement;
    if (contrast === 'high') root.setAttribute('data-contrast', 'high');
    else root.removeAttribute('data-contrast');
  }, [contrast, storageRead]);

  // Podążanie za ustawieniem systemowym tylko w trybie 'system' — i dopiero, gdy
  // wiemy, że 'system' to naprawdę zapisana preferencja, a nie wartość domyślna.
  useEffect(() => {
    if (!storageRead || theme !== 'system') return;
    const mq = window.matchMedia('(prefers-color-scheme: dark)');
    const onChange = () => {
      const resolved = systemMode();
      setResolvedTheme(resolved);
      const root = document.documentElement;
      root.setAttribute('data-mode', resolved);
      root.classList.remove('light', 'dark');
      root.classList.add(resolved);
      root.style.colorScheme = resolved;
    };
    mq.addEventListener('change', onChange);
    return () => mq.removeEventListener('change', onChange);
  }, [theme, storageRead]);

  const setTheme = useCallback(
    (next: ThemeMode) => {
      setThemeState(next);
      persist(storageKey, next);
    },
    [storageKey]
  );

  const setDensity = useCallback((next: ThemeDensity) => {
    setDensityState(next);
    persist(DENSITY_STORAGE_KEY, next);
  }, []);

  const setContrast = useCallback((next: ThemeContrast) => {
    setContrastState(next);
    persist(CONTRAST_STORAGE_KEY, next);
  }, []);

  const toggleTheme = useCallback(() => {
    setTheme(resolvedTheme === 'dark' ? 'light' : 'dark');
  }, [resolvedTheme, setTheme]);

  return (
    <ThemeContext.Provider
      value={{
        theme,
        resolvedTheme,
        setTheme,
        toggleTheme,
        density,
        setDensity,
        contrast,
        setContrast,
      }}
    >
      {children}
    </ThemeContext.Provider>
  );
}

export function useTheme() {
  const context = useContext(ThemeContext);
  if (context === undefined) {
    throw new Error('useTheme must be used within a ThemeProvider');
  }
  return context;
}

export { ThemeContext };
